import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from '~/app/hooks'
import { Order } from '~/components/EFTable/EFTable.model'
import { pageSetting } from '~/redux/features'

interface IDataTableHook<T, S> {
  _defaultOrderBy: keyof T
  _defaultOrder?: Order
  _searchParamsObject: S
}
export const useDataTable = <T, S>({
  _defaultOrderBy,
  _defaultOrder,
  _searchParamsObject
}: IDataTableHook<T, S>) => {
  const { pageSize: _pageSize } = useAppSelector(pageSetting)
  const [order, setOrder] = useState<Order>(_defaultOrder ?? 'asc')
  const [orderBy, setOrderBy] = useState<keyof T>(_defaultOrderBy)
  const [searchParams, setSearchParams] = useSearchParams()
  const _searchTerms = searchParams.get('searchTerms') ?? ''
  const _page = searchParams.get('page') ?? '1'
  const [searchParamsObject, setSearchParamsObject] = useState<S>({
    ..._searchParamsObject,
    page: _page,
    pageSize: _pageSize,
    searchTerms: _searchTerms
  } as unknown as S)

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof T) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const searchQueryChanges = ({
    page = _page ?? '1',
    pageSize = _pageSize ?? '20',
    ...rest
  }: any): any => {
    rest.pageSize = pageSize !== '' && pageSize !== '20' ? pageSize : ''
    rest.page = page !== '' && page !== '1' ? page : ''

    // Return the object with keys have values only
    return Object.entries(rest).reduce((result, [key, value]) => {
      if (value) {
        result[key as any] = value
      }
      return result
    }, {} as Partial<any>) as any
  }

  type OtherParams = Record<any, any>
  const handleSearch = (searchTerms: string, other?: OtherParams) => {
    const defaultParams = {
      ...searchParamsObject,
      page: '1',
      searchTerms: searchTerms
    }

    const mergedParams = other ? { ...defaultParams, ...other } : defaultParams

    setSearchParams(searchQueryChanges(mergedParams))
  }

  const handleChangePage = (newPage: number) => {
    const updatedPage = (newPage + 1).toString()
    setSearchParams(
      searchQueryChanges({
        ...searchParamsObject,
        page: updatedPage
      })
    )
  }

  useEffect(() => {
    setSearchParamsObject(prevState => ({
      ...prevState,
      searchTerms: _searchTerms ?? '',
      page: _page ?? '1',
      pageSize: _pageSize ?? '20'
    }))
  }, [_searchTerms, _page, _pageSize])

  return {
    order,
    orderBy,
    searchParamsObject,
    searchQueryChanges,
    setSearchParamsObject,
    handleChangePage,
    handleRequestSort,
    handleSearch
  }
}
